import { ComponentProps, useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { shuffle } from 'lodash-es'

import {
  AllocationForm,
  calculateRemaining,
  canContinue,
  formatValue,
  isExclusiveDisabled,
  isMinusDisabled,
  isPlusDisabled,
  Question as QuestionType,
  getCounterLabel,
} from 'utils/allocation'
import { useConcept } from 'hooks/concepts'

import AdvanceButton, { AdvanceButtonContainer } from './AdvanceButton'
import AllocationControl from './AllocationControl'
import AllocationCounters from './AllocationCounters'
import { AdvancedImage } from '@cloudinary/react'
import { getImageFromPublicID } from 'utils/media'
import ImageZoomControls from './ImageZoomControls'

import Question from './Question'
import InputCheckbox from './InputCheckbox'

interface AllocationQuestionProps {
  buttonText?: ComponentProps<typeof AdvanceButton>['buttonText']
  isLastQuestion: boolean
  onCompletedQuestion(opts: Pick<QuestionType, 'response'>): void
  onPaste(freeText: string): void
  onViewedConcept(questionID: string, conceptID: string): void
  onViewedImage(optionID: string): void
  question: QuestionType
}

const AllocationQuestion = ({
  buttonText,
  isLastQuestion,
  onCompletedQuestion,
  onPaste,
  onViewedConcept,
  onViewedImage,
  question,
}: AllocationQuestionProps) => {
  const { handleSubmit, setValue, watch } = useForm<AllocationForm>({
    defaultValues: {
      allocations: question.response,
    },
  })

  const allocations = watch('allocations')
  const [apiError, setApiError] = useState<string | null>(null)

  // Create refs for each allocation input
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const sortedOptions = useMemo(() => {
    const optionsBySort = [...question.options].sort((a, b) => a.sort - b.sort)

    const randomizeOptions = question.features.randomizeOptions
    if (!randomizeOptions) {
      return optionsBySort
    }

    const { anchoredResourceIDs } = randomizeOptions
    const shuffledOptions = shuffle(
      optionsBySort.filter((option) => !anchoredResourceIDs.has(option.id)),
    )
    return optionsBySort.map((option) =>
      anchoredResourceIDs.has(option.id)
        ? option
        : (shuffledOptions.shift() as (typeof optionsBySort)[number]),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id])

  // Calculate counters in real-time
  const remaining = calculateRemaining(
    question.constraints.totalChips,
    allocations,
  )
  // Validate allocation and determine if Next button should be enabled
  const validation = canContinue(question, allocations)
  const canAdvance = validation.isValid

  // Handle form submission
  const onSubmit: SubmitHandler<AllocationForm> = async (data) => {
    try {
      setApiError(null) // Clear any previous errors
      await onCompletedQuestion({
        response: data.allocations,
      })
    } catch (error) {
      // Display error message to the respondent
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while submitting your response. Please try again.'
      setApiError(errorMessage)
    }
  }

  // Handle increment for a specific option
  const handleIncrement = (optionId: string) => {
    const currentValue = allocations[optionId] || 0
    const option = question.options.find((opt) => opt.id === optionId)
    const maxConstraint = option?.constraints.max ?? null
    const stepIncrement = question.constraints.stepIncrement

    // Block if incrementing by full step would exceed remaining or max constraint
    if (remaining < stepIncrement) {
      return
    }
    if (
      maxConstraint !== null &&
      currentValue + stepIncrement > maxConstraint
    ) {
      return
    }

    setValue('allocations', {
      ...allocations,
      [optionId]: currentValue + stepIncrement,
    })
  }

  // Handle decrement for a specific option
  const handleDecrement = (optionId: string) => {
    const currentValue = allocations[optionId] || 0
    const option = question.options.find((opt) => opt.id === optionId)
    const minConstraint = option?.constraints.min ?? 0
    const stepIncrement = question.constraints.stepIncrement

    // Block if decrementing by full step would go below min constraint
    if (currentValue - stepIncrement < minConstraint) {
      return
    }

    setValue('allocations', {
      ...allocations,
      [optionId]: currentValue - stepIncrement,
    })
  }

  // Handle direct input change for a specific option
  const handleChange = (optionId: string, newValue: number) => {
    const option = question.options.find((opt) => opt.id === optionId)
    const maxConstraint =
      option?.constraints.max ?? question.constraints.totalChips
    const minConstraint = option?.constraints.min ?? 0
    const stepIncrement = question.constraints.stepIncrement

    if (
      newValue > maxConstraint ||
      newValue < minConstraint ||
      newValue % stepIncrement !== 0
    ) {
      return
    }

    setValue('allocations', {
      ...allocations,
      [optionId]: newValue,
    })
  }

  const handleExclusiveChecked = (optionId: string, isChecked: boolean) => {
    if (isChecked && remaining < question.constraints.totalChips) {
      return
    }
    setValue('allocations', {
      ...allocations,
      [optionId]: isChecked ? question.constraints.totalChips : 0,
    })
  }

  const { concept } = useConcept({ onViewedConcept, question })

  // Set initial focus to first allocation input on question load
  // This handles both initial question load and back navigation
  useEffect(() => {
    if (sortedOptions.length > 0) {
      const firstOptionId = sortedOptions[0].id
      const firstInput = inputRefs.current[firstOptionId]
      if (firstInput) {
        // Use setTimeout to ensure the input is fully rendered
        setTimeout(() => {
          firstInput.focus()
        }, 0)
      }
    }
  }, [question.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <Question
        concept={concept}
        directions={question.directions}
        title={question.title}
      >
        <div className="space-y-6">
          <AllocationCounters
            remaining={remaining}
            total={question.constraints.totalChips}
            unitType={question.display.unitType}
          />

          <div className="space-y-3">
            {sortedOptions.map((option) => {
              const value = allocations[option.id] || 0
              const isExclusive =
                question.constraints.exclusiveOptionIDs &&
                question.constraints.exclusiveOptionIDs.has(option.id)
              const exclusiveChecked =
                allocations[option.id] === question.constraints.totalChips
              const exclusiveDisabled =
                !exclusiveChecked &&
                isExclusiveDisabled(remaining, question.constraints.totalChips)
              const minDisabled = isMinusDisabled(value, option.constraints.min)
              const plusDisabled = isPlusDisabled(
                remaining,
                value,
                option.constraints.max,
              )

              return (
                <div
                  key={option.id}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3"
                >
                  <div className="flex items-center gap-3 text-gray-700">
                    {option.type === 'image' && (
                      <div className="relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded">
                        <AdvancedImage
                          alt={option.image.alt}
                          className="max-h-full max-w-full object-contain"
                          cldImg={getImageFromPublicID({
                            publicID: option.image.publicID,
                          })}
                        />
                        <ImageZoomControls
                          alt={option.image.alt}
                          image={getImageFromPublicID({
                            publicID: option.image.publicID,
                          })}
                          onClickZoom={() => {
                            onViewedImage(option.id)
                          }}
                          zoomRequired={
                            question.constraints.requireImageViews &&
                            option.constraints.requireView &&
                            !option.image.viewed
                          }
                        />
                      </div>
                    )}
                    <span>
                      {option.type === 'image' && option.features.hideTitle
                        ? ''
                        : option.title}
                      {question.constraints.useCarryOverOptionAsMax &&
                        option.constraints.max !== null && (
                          <span className="text-gray-500">
                            {' '}
                            (Max:{' '}
                            {formatValue(
                              option.constraints.max,
                              question.display.unitType,
                            )}
                            )
                          </span>
                        )}
                    </span>
                  </div>

                  {!isExclusive ? (
                    <AllocationControl
                      ref={(el) => {
                        inputRefs.current[option.id] = el
                      }}
                      ariaLabel={option.title}
                      disabled={false}
                      maxValue={Math.min(
                        remaining + value,
                        option.constraints.max ?? Infinity,
                      )}
                      minusDisabled={minDisabled}
                      onChange={(newValue) => handleChange(option.id, newValue)}
                      onDecrement={() => handleDecrement(option.id)}
                      onIncrement={() => handleIncrement(option.id)}
                      onPaste={onPaste}
                      plusDisabled={plusDisabled}
                      stepIncrement={question.constraints.stepIncrement}
                      unitType={question.display.unitType}
                      value={value}
                    />
                  ) : (
                    <InputCheckbox
                      aria-label={option.title}
                      checked={exclusiveChecked}
                      disabled={exclusiveDisabled}
                      id={option.id}
                      onChange={() =>
                        handleExclusiveChecked(option.id, !exclusiveChecked)
                      }
                    />
                  )}
                </div>
              )
            })}
          </div>

          <div className="flex justify-end">
            <div
              aria-atomic="true"
              aria-live="polite"
              className="text-lg font-medium text-gray-700"
              role="status"
            >
              {getCounterLabel(
                'current',
                question.constraints.totalChips - remaining,
                question.display.unitType,
              )}
            </div>
          </div>

          {apiError && (
            <div
              aria-live="assertive"
              className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              role="alert"
            >
              {apiError}
            </div>
          )}
        </div>
      </Question>

      <AdvanceButtonContainer>
        <AdvanceButton
          buttonText={buttonText}
          disabled={!canAdvance}
          isLastQuestion={isLastQuestion}
        />
      </AdvanceButtonContainer>
    </form>
  )
}

export default AllocationQuestion
